import i18n from "app/i18n";
import { DateTime } from "luxon";
import { dayHours, generateHoursWithLabels } from "../../../utils/timeUtils";

export const attendance_buttons = () => [
  {
    value: 0,
    label: i18n.t("On"),
  },
  {
    value: 1,
    label: i18n.t("Off"),
  },
];

export const privacy_options = [
  {
    value: "private",
    label: i18n.t("Private"),
  },
  {
    value: "public",
    label: i18n.t("Public"),
  },
];

export const cancellation_buttons = () => [
  {
    value: 0,
    label: i18n.t("Start Time"),
    tooltip: i18n.t("Select a cut-off time based on the program's start time."),
  },
  {
    value: 1,
    label: i18n.t("Static"),
    tooltip: i18n.t(
      "Select a cut-off time that applies to the program on a given day.",
    ),
  },
];

export const display_clinic_buttons = () => [
  {
    value: 0,
    label: i18n.t('Series'),
    tooltip: [
      i18n.t('Your clinics will appear as one grouped listing.'),
      i18n.t('Participants can view all available dates and sign up for individual sessions on a single page.')
    ]
  },
  {
    value: 1,
    label: i18n.t('Individual'),
    tooltip: [
      i18n.t('Create single clinic dates in bulk.'),
      i18n.t('Each clinic will appear as its own separate listing.')
    ]
  },
];

export const registration_open_buttons = () => [
  {
    value: 0,
    label: i18n.t('Date'),
    tooltip: i18n.t('Registration opens on the same date for all clinics. Be sure to open registration before the first clinic.'),
  },
  {
    value: 1,
    label: i18n.t('Rolling'),
    tooltip: i18n.t('Registration for each clinic opens at a set interval. For example, registration for each clinic opens 7 days before the clinic date at 10am.'),
  },
];

export const registration_close_buttons = () => [
  {
    value: 0,
    label: i18n.t('Date'),
    tooltip: i18n.t('Registration closes on the same date for all clinics.'),
  },
  {
    value: 1,
    label: i18n.t('Rolling'),
    tooltip: i18n.t('Registration for each clinic closes at a set interval. For example, registration for each clinic closes 1 day before the clinic date at 5pm.'),
  },
];

export const repeat_options = (includeCustom?: boolean) => {
  const options = [
    { label: i18n.t("Does not repeat"), value: "Never" },
    { label: i18n.t("Daily"), value: "daily" },
    { label: i18n.t("Weekly"), value: "weekly" },
    { label: i18n.t("Bi-weekly"), value: "biweekly" },
    { label: i18n.t("Monthly"), value: "monthly" },
    { label: i18n.t("Custom"), value: "custom" },
  ];

  if (includeCustom) {
    options.push();
  }

  return options;
};

const getReminders = () => {
  const reminders = [
    { value: 1, label: i18n.t("count_hour_uppercase", { count: 1 }) },
    { value: 3, label: i18n.t("count_hour_uppercase", { count: 3 }) },
    { value: 6, label: i18n.t("count_hour_uppercase", { count: 6 }) },
    { value: 12, label: i18n.t("count_hour_uppercase", { count: 12 }) },
    { value: 24, label: i18n.t("count_day_uppercase", { count: 1 }) },
  ];

  for (let i = 2; i < 30; i++) {
    reminders.push({ value: i * 24, label: i18n.t("count_day_uppercase", { count: i }) });
  }

  return reminders;
};

export const cut_off_list = () => [{ value: -1, label: i18n.t("Unlimited") }, ...getReminders()];
export const cut_off_days = () => [
  { value: 0, label: i18n.t("Day Of") },
  { value: 1, label: i18n.t("Day Before") },
  { value: 2, label: i18n.t("count_day_before", { count: 2 }) },
  { value: 3, label: i18n.t("count_day_before", { count: 3 }) },
];

const findCutOffTime = (attributes, timeFormat: "12h" | "24h" = "12h") => {
  let cutOffTime = null;
  let cutOffDay = null;
  if (attributes.cancellationCutOffType === "start_time") {
    cutOffDay = attributes.cancellationCutOffTime;
    cutOffDay = cut_off_list().find((item) => item.value === cutOffDay);
    return { cutOffDay, cutOffTime };
  } else {
    const days = Math.floor(attributes.cancellationCutOffTime / 24);
    cutOffTime = attributes.cancellationCutOffTime - days * 24;
    const options = dayHours(timeFormat);
    cutOffTime = options.find((item) => item.value === cutOffTime);
    cutOffDay = cut_off_days().find((item) => item.value === days);
    return { cutOffDay, cutOffTime };
  }
};

const getDate = (date, timezone) => {
  // Convert UTC timestamp to the program's timezone first
  const dateInTimezone = DateTime.fromISO(date, { zone: timezone });

  // Extract date components and return as UTC ISO string at midnight
  return DateTime.utc(
    dateInTimezone.year,
    dateInTimezone.month,
    dateInTimezone.day,
  ).toISO();
};

export const constructCloneProps = (
  attributes,
  edit,
  view,
  schedulingInterval = 15,
  timeFormat: "12h" | "24h" = "12h",
) => {
  const hoursWithLables = generateHoursWithLabels(schedulingInterval, timeFormat);
  const { cutOffDay, cutOffTime } = findCutOffTime(attributes, timeFormat);
  const tags = attributes?.tags?.map((item) => {
    return { label: item.name, value: item.id };
  });
  return {
    id: edit ? attributes.id : null,
    type:
      attributes?.attendance?.charAt(0).toUpperCase() +
      attributes?.attendance?.slice(1),
    category: attributes?.category,
    title: view
      ? attributes.title
      : edit
        ? attributes.title
        : `[Clone Of] ${attributes.title}`,
    subtitle: attributes.subtitle,
    page: 1,
    email: attributes.email,
    phone: attributes.phone,
    location: attributes.location,
    tab: 1,
    capacity: attributes.capacity,
    price: attributes.price,
    isFree: attributes.free ?? attributes.isFree ?? false,
    shortDescription: attributes.shortDescription,
    longDescription: attributes.longDescription,
    thumbnailURL: attributes.externalThumbnailUrl,
    headerURL: attributes.externalHeaderUrl,
    flyerURL: attributes.flyerPdfUrl,
    tags,
    coachIds: attributes?.coaches?.map((item) => item.id),
    date: getDate(attributes?.start, attributes?.facilityTimeZone),
    startTime: hoursWithLables.find(
      (item) => item.value === attributes?.startTime,
    ),
    endTime: hoursWithLables.find((item) => item.value === attributes?.endTime),
    waitlist: attributes.withWaitlist,
    registrationOpenDate: attributes?.registrationStartTime,
    registrationOpenTime: hoursWithLables.find(
      (item) => item.value === attributes.registrationParsedStartTime,
    ),
    registrationCloseDate: attributes?.registrationEndTime,
    registrationCloseTime: hoursWithLables.find(
      (item) => item.value === attributes.registrationParsedEndTime,
    ),
    registrationStartTimeOffset: attributes?.registrationStartTimeOffset,
    registrationEndTimeOffset: attributes?.registrationEndTimeOffset,
    cutOffType: attributes.cancellationCutOffType,
    cutOffDay,
    cutOffTime,
    edit,
    spotsBooked: attributes.spotsBooked,
    status: attributes.status,
    events: attributes.events,
    repeat: edit
      ? repeat_options().find(
          (item) => item.value.toLowerCase() === attributes.repeat,
        )
      : null,
    untilDate: edit ? attributes.untilDate : null,
    facilityId: attributes.facilityId,
    currency: generateCurrencySymbol(attributes.facilityCurrency),
    timeZone: attributes.facilityTimeZone,
    registrationStatus: attributes.registrationStatus,
    privacy: attributes.privacy,
    restrictAdditionalPeople: attributes.restrictAdditionalPeople,
    canEditRegistrationSettings: edit ? (attributes.canEditRegistrationSettings ?? true) : true,
    canExtendRegistrationEndTime: edit ? (attributes.canExtendRegistrationEndTime ?? true) : true,
    canEditRegistrationDates: edit ? (attributes.canEditRegistrationDates ?? true) : true,
    canEditPaymentCollector: edit ? (attributes.canEditPaymentCollector ?? true) : true,
    canEditPaymentMethod: edit ? (attributes.canEditPaymentMethod ?? true) : true,
    collectType: attributes.collectType || 'offline',
    onlineDefault: attributes.onlineDefault || false,
    paymentCollectorCoachProfileId: attributes.paymentCollectorCoachProfileId || null,
    coaches: attributes.coachProfiles || [],
  };
};

export const getColorCode = (status) => {
  switch (status) {
    case "open":
      return {
        background: "#43A0471A",
        text: "#43A047",
      };
    case "not_started":
      return {
        background: "#E7E7E7",
        text: "#000000",
      };
    case "registered":
      return {
        background: "#E545001A",
        text: "#E54500",
      };
    case "waitlisted":
      return {
        background: "#E545001A",
        text: "#E54500",
      };
    case "closed":
      return {
        background: "#E7E7E7",
        text: "#8C8C8C",
      };
  }

  return {
    background: "#E7E7E7",
    text: "#8C8C8C",
  };
};

export const generateCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case "USD":
      return "$";
    case "AUD":
      return "$";
    case "GBP":
      return "£";
    case "CAD":
      return "$";
    case "JPY":
      return "¥";
    case "AED":
      return "AED";
    case "EUR":
      return "€";
    case "MXN":
      return "MXN";
    case "NZD":
      return "$";
    case "TRY":
      return "₺";
    case "CNY":
      return "¥¥";
    case "ZAR":
      return "R";
    case "CHF":
      return "SFr.";
    case "NOK":
      return "kr";
    case "CZK":
      return "Kč";
    case "HKD":
      return "HK$";
    case "THB":
      return "฿";
    default:
      return "$";
  }
};

export const getCurrencyCodeFromSymbol = (symbol: string, fallbackCode?: string): string => {
  const map: Record<string, string> = {
    "£": "GBP",
    "¥": "JPY",
    "€": "EUR",
    "₺": "TRY",
    "¥¥": "CNY",
    "R": "ZAR",
    "SFr.": "CHF",
    "kr": "NOK",
    "Kč": "CZK",
    "HK$": "HKD",
    "฿": "THB",
    "AED": "AED",
    "MXN": "MXN",
  };

  if (map[symbol]) {
    return map[symbol];
  }

  // "$" is ambiguous (USD, AUD, CAD, NZD) — use fallbackCode if it also maps to "$"
  if (symbol === "$" && fallbackCode) {
    const fallbackSymbol = generateCurrencySymbol(fallbackCode);
    if (fallbackSymbol === "$") {
      return fallbackCode.toUpperCase();
    }
  }

  return fallbackCode?.toUpperCase() || "USD";
};

export const generateCurrencySymbolPriceAbbreviation = (currency: string, price: string) => {
  const symbol: string = generateCurrencySymbol(currency);

  return `${symbol}${price} ${symbol !== currency?.toUpperCase() ? currency?.toUpperCase() : ''}`;
};
